import { useState } from "react";
import { Link } from "react-router-dom";
import { useSentryIssues, SentryIssue, StatsPeriod } from "@/hooks/useSentryIssues";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  AlertTriangle, 
  ArrowLeft, 
  ExternalLink, 
  RefreshCw,
  Bug,
  Users,
  Clock,
  AlertCircle,
  CheckCircle2,
  EyeOff
} from "lucide-react";
import { PiktableLoader } from "@/components/PiktableLoader";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const periodLabels: Record<StatsPeriod, string> = {
  "24h": "Dernières 24 heures",
  "7d": "7 derniers jours",
  "14d": "14 derniers jours",
  "30d": "30 derniers jours",
};

const levelColors: Record<string, string> = {
  fatal: "bg-red-600 text-white",
  error: "bg-red-500/10 text-red-600 border-red-500/30",
  warning: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  info: "bg-blue-500/10 text-blue-600 border-blue-500/30",
};

const statusIcons: Record<string, React.ReactNode> = {
  unresolved: <AlertCircle className="w-4 h-4 text-red-500" />,
  resolved: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  ignored: <EyeOff className="w-4 h-4 text-muted-foreground" />,
};

export default function AdminErrors() {
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>("24h");
  const { data: issues, isLoading, error, refetch, isFetching } = useSentryIssues(statsPeriod);

  const stats = {
    total: issues?.length || 0,
    unresolved: issues?.filter(i => i.status === "unresolved").length || 0,
    errors: issues?.filter(i => i.level === "error" || i.level === "fatal").length || 0,
    usersImpacted: issues?.reduce((sum, i) => sum + (i.userCount || 0), 0) || 0,
    totalOccurrences: issues?.reduce((sum, i) => sum + parseInt(i.count || "0"), 0) || 0,
  };

  if (isLoading) {
    return <PiktableLoader message="Chargement des erreurs Sentry..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-serif font-bold flex items-center gap-3">
                <Bug className="w-7 h-7 text-red-500" />
                Erreurs Sentry
              </h1>
              <p className="text-muted-foreground">
                Suivi des erreurs en production
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={statsPeriod} onValueChange={(v) => setStatsPeriod(v as StatsPeriod)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(periodLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              Rafraîchir
            </Button>
            <a 
              href="https://piktable.sentry.io/issues/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline">
                <ExternalLink className="w-4 h-4 mr-2" />
                Dashboard Sentry
              </Button>
            </a>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <Card className="border-red-500/50 bg-red-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <p className="font-medium">Erreur lors du chargement</p>
                  <p className="text-sm text-muted-foreground">
                    {error instanceof Error ? error.message : "Une erreur est survenue"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Bug className="w-4 h-4" />
                Total issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total}</div>
              <p className="text-sm text-muted-foreground">{periodLabels[statsPeriod].toLowerCase()}</p>
            </CardContent>
          </Card>

          <Card className="border-red-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Non résolues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{stats.unresolved}</div>
              <p className="text-sm text-muted-foreground">à traiter</p>
            </CardContent>
          </Card>

          <Card className="border-amber-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Erreurs critiques
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{stats.errors}</div>
              <p className="text-sm text-muted-foreground">error + fatal</p>
            </CardContent>
          </Card>

          <Card className="border-blue-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                Utilisateurs impactés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.usersImpacted}</div>
              <p className="text-sm text-muted-foreground">unique</p>
            </CardContent>
          </Card>

          <Card className="border-purple-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-purple-500" />
                Occurrences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{stats.totalOccurrences}</div>
              <p className="text-sm text-muted-foreground">événements</p>
            </CardContent>
          </Card>
        </div>

        {/* Issues Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des issues</CardTitle>
            <CardDescription>
              Erreurs groupées par type et stack trace
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!issues || issues.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bug className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Aucune erreur</p>
                <p className="text-sm">Parfait ! Aucune erreur capturée sur cette période.</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Statut</TableHead>
                      <TableHead className="w-20">Niveau</TableHead>
                      <TableHead>Erreur</TableHead>
                      <TableHead className="w-24 text-right">Events</TableHead>
                      <TableHead className="w-24 text-right">Users</TableHead>
                      <TableHead className="w-40">Dernière vue</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {issues.map((issue) => (
                      <TableRow key={issue.id} className="group">
                        <TableCell>
                          <div className="flex items-center justify-center">
                            {statusIcons[issue.status] || statusIcons.unresolved}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={levelColors[issue.level] || levelColors.error}
                          >
                            {issue.level}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium text-sm line-clamp-1">
                              {issue.title}
                            </p>
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {issue.culprit}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {issue.shortId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {parseInt(issue.count).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {issue.userCount}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(issue.lastSeen), { 
                            addSuffix: true, 
                            locale: fr 
                          })}
                        </TableCell>
                        <TableCell>
                          <a 
                            href={issue.permalink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Button variant="ghost" size="icon">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
